import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Check, ChevronLeft, Users, X } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, EnumOption, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  fetchCommunity,
  resolveCommunity,
  LOC_STR as S,
  type CommunitySubmissionRow,
} from './localizationExtraApi'

/**
 * A22.08 — Народный перевод (сообщество). Moderation queue for crowd-submitted
 * translations (approve → promote to the pack, reject → discard). Dormant until
 * `/admin/localization/community` ships → fetcher fail-soft to empty.
 * Placeholder permission USERS_VIEW.
 */
export function LocalizationCommunity() {
  const { toast } = useToast()

  const statusOptions = useMemo<EnumOption[]>(
    () => [
      { value: 'pending', label: S.statusPending, status: 'pending' },
      { value: 'approved', label: S.statusApproved, status: 'active' },
      { value: 'rejected', label: S.statusRejected, status: 'blocked' },
    ],
    [],
  )

  const columns = useMemo<AdminColumn<CommunitySubmissionRow>[]>(
    () => [
      { key: 'key', header: S.key, type: 'text' },
      { key: 'lang', header: S.language, type: 'text' },
      { key: 'suggestion', header: S.suggestion, type: 'longtext' },
      { key: 'current', header: S.current, type: 'longtext', hiddenByDefault: true },
      { key: 'author', header: S.author, type: 'text' },
      { key: 'votes', header: S.votes, type: 'number' },
      { key: 'status', header: S.status, type: 'enum', options: statusOptions },
      { key: 'submitted_at', header: S.submittedAt, type: 'date' },
    ],
    [statusOptions],
  )

  const resolve = useCallback(
    async (row: CommunitySubmissionRow, action: 'approve' | 'reject') => {
      try {
        await resolveCommunity(row.id, action)
        toast(action === 'approve' ? S.approved : S.rejected, { tone: 'success' })
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<CommunitySubmissionRow>[]>(
    () => [
      {
        id: 'approve',
        label: S.approve,
        icon: <Check size={14} />,
        onSelect: (r) => resolve(r, 'approve'),
        visible: (r) => r.status === 'pending',
      },
      {
        id: 'reject',
        label: S.reject,
        icon: <X size={14} />,
        danger: true,
        onSelect: (r) => resolve(r, 'reject'),
        visible: (r) => r.status === 'pending',
      },
    ],
    [resolve],
  )

  const fetcher = useCallback(fetchCommunity, [])
  return (
    <>
      <PageHeader
        title={S.communityTitle}
        screenId="A22.08"
        actions={
          <Link
            to="/localization"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {S.back}
          </Link>
        }
      />
      <DataTable<CommunitySubmissionRow>
        screenId="A22.08"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Users size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
